import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import UpdateRoundedIcon from '@mui/icons-material/UpdateRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import APIService from 'services/APIService'
import { cardStyle, font, ink, line, radius, surface, transition, labelStyle } from 'features/dashboard/tokens'
import { RAG, scoreColour, scoreBand, formatPercent } from 'features/dashboard/charts/scoreColour'
import ComplianceScoreExplainer from 'features/compliance/ComplianceScoreExplainer'
import ComplianceSetupExplainer from 'features/compliance/ComplianceSetupExplainer'
import TasksRemedialsExplainer from 'features/taskManagement/TasksRemedialsExplainer'
import { PRIVATE_PATHS } from 'utils/paths'
import { DASHBOARD, remedialsViewUrl } from 'features/actions/workspace/remedialsQuery'
import { RemedialCountsExplainer } from 'features/actions/workspace/Explainers'

const api = APIService()
const summaryCache = new Map()

const G = RAG.low.fg
const A = RAG.medium.fg
const O = RAG.high.fg
const R = RAG.critical.fg

const StatCell = ({ icon: Icon, tint, value, label }) => {
  const c = value > 0 ? tint : ink.mute
  return (
    <Cell title={`${value} ${label}`}>
      <Icon style={{ fontSize: 16, color: c }} />
      <Num style={{ color: c }}>{value}</Num>
      <Lbl>{label}</Lbl>
    </Cell>
  )
}

const LinkStatCell = ({ icon: Icon, tint, value, label, hint, onClick }) => {
  const c = value > 0 ? tint : ink.mute
  return (
    <LinkCell type='button' onClick={onClick} title={hint} aria-label={`${value} ${label}. ${hint}`}>
      <Icon style={{ fontSize: 16, color: c }} aria-hidden='true' />
      <Num style={{ color: c }}>{value}</Num>
      <Lbl>{label}</Lbl>
    </LinkCell>
  )
}

const scopeFor = (bpId, portfolioId, estateId) => {
  if (bpId) {
    return {
      endpoint: `/building-passport/${bpId}/compliance/summary`,
      compliance: `/bp-list/${bpId}/compliance?section=programme`,
      complianceLabel: 'Open compliance',
      complianceTitle: 'Open the compliance tracker for this building',
      tasks: `/tasks?bp=${bpId}&from=${bpId}`,
      report: `/bp-list/${bpId}/incidents?report=1`,
      incidents: `/bp-list/${bpId}/incidents`,
      remedialTarget: { bpId },
    }
  }
  const type = portfolioId ? 'portfolio' : estateId ? 'estate' : null
  if (!type) return null
  const id = portfolioId || estateId
  const base = type === 'portfolio' ? `/portfolios/${id}` : `/estates/${id}`
  return {
    endpoint: `${base}/compliance/summary`,
    compliance: `${PRIVATE_PATHS.dashboard}?altitude=${type}&entityId=${id}`,
    complianceLabel: 'Compliance dashboard',
    complianceTitle: `Open the compliance dashboard for this ${type}`,
    tasks: `${base}?tab=tasks`,
    report: null,
    incidents: `${base}?tab=incidents`,
    remedialTarget: type === 'portfolio' ? { portfolioId: id } : { estateId: id },
  }
}

const CompliancePulse = ({ bpId, portfolioId, estateId, showCompliance = true, showTasks = true, showIncidents = true, compact = false }) => {
  const navigate = useNavigate()
  const scope = scopeFor(bpId, portfolioId, estateId)
  const endpoint = scope?.endpoint || null
  const [summary, setSummary] = useState(() => (endpoint ? summaryCache.get(endpoint) : null) || null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!endpoint) return undefined
    let cancelled = false
    setSummary(summaryCache.get(endpoint) || null)
    setFailed(false)
    api.get(endpoint)
      .then(res => {
        const data = res?.data?.data || null
        if (data) summaryCache.set(endpoint, data)
        if (!cancelled) setSummary(data)
      })
      .catch(() => { if (!cancelled && !summaryCache.has(endpoint)) setFailed(true) })
    return () => { cancelled = true }
  }, [endpoint])

  if (!scope) return null

  if (failed) return null
  if (!showCompliance && !showTasks && !showIncidents) return null

  if (!summary) {
    return (
      <Strip aria-busy='true' $compact={compact}>
        <Overview $compact={compact}>
          <SkeletonRing $compact={compact} />
          <SkeletonBlock>
            <SkeletonLine style={{ width: 150 }} />
            <SkeletonLine style={{ width: 120 }} />
          </SkeletonBlock>
        </Overview>
      </Strip>
    )
  }

  const score = summary.score ?? null
  const scored = score !== null && score !== undefined
  const tint = scoreColour(score)
  const band = scored ? scoreBand(score) : 'Not scored yet'
  const setup = summary.setup || {}
  const setupPercent = setup.percent
  const t = summary.tasks || {}
  const i = summary.incidents || {}
  const r = summary.remedials || {}
  const goProgramme = () => navigate(scope.compliance)

  const hero = showCompliance ? (
    <Hero key='compliance' $compact={compact}>
      <SecLabel><AssignmentTurnedInOutlinedIcon style={{ fontSize: 15 }} aria-hidden='true' />Compliance<ComplianceScoreExplainer align='left' /></SecLabel>
      <HeroRow>
        <RingButton type='button' onClick={goProgramme} title={scope.complianceTitle}>
          <Ring $colour={tint} $deg={(scored ? score : 0) * 3.6} $compact={compact}>
            <RingInner $compact={compact}>{formatPercent(score)}</RingInner>
          </Ring>
          <Band $tint={scored ? tint : ink.mute}>{band}</Band>
        </RingButton>
        <HeroMeta>
          <Stats>
            <StatCell icon={CheckCircleRoundedIcon} tint={G} value={summary.satisfied ?? 0} label='satisfied' />
            <StatCell icon={CancelRoundedIcon} tint={R} value={summary.expired ?? 0} label='expired' />
            <StatCell icon={ScheduleRoundedIcon} tint={A} value={summary.pending ?? 0} label='pending' />
          </Stats>
          <SetupLine>
            <SetupLabel>Setup<ComplianceSetupExplainer align='left' /></SetupLabel>
            <Track><Fill style={{ width: `${setupPercent ?? 0}%` }} /></Track>
            <SetupValue>{setupPercent === null || setupPercent === undefined ? '—' : `${Math.round(setupPercent)}%`}</SetupValue>
          </SetupLine>
          {summary.unmetStatutory > 0 && (
            <Warning><ErrorOutlineRoundedIcon style={{ fontSize: 14 }} /> {summary.unmetStatutory} unmet statutory</Warning>
          )}
        </HeroMeta>
      </HeroRow>
      {compact
        ? <TileBtn type='button' $compact onClick={goProgramme}>{scope.complianceLabel}</TileBtn>
        : <PrimaryBtn type='button' onClick={goProgramme}>{scope.complianceLabel}</PrimaryBtn>}
    </Hero>
  ) : null

  const tasksSection = showTasks ? (
    <Section key='tasks' $compact={compact}>
      <SecLabel><AssignmentOutlinedIcon style={{ fontSize: 15 }} aria-hidden='true' />Tasks<TasksRemedialsExplainer align='left' /></SecLabel>
      <Stats>
        <StatCell icon={ScheduleRoundedIcon} tint={A} value={t.open || 0} label='open' />
        <StatCell icon={ErrorOutlineRoundedIcon} tint={R} value={t.overdue || 0} label='overdue' />
        <StatCell icon={HelpOutlineRoundedIcon} tint={O} value={t.unassigned || 0} label='unassigned' />
        <StatCell icon={CheckCircleRoundedIcon} tint={G} value={t.completed || 0} label='completed' />
        <StatCell icon={UpdateRoundedIcon} tint={A} value={t.completedLate || 0} label='completed late' />
      </Stats>
      <TileBtn type='button' $compact={compact} onClick={() => navigate(scope.tasks)}>View tasks</TileBtn>
    </Section>
  ) : null

  const incidentsSection = showIncidents ? (
    <Section key='incidents' $compact={compact}>
      <SecLabel><ReportProblemOutlinedIcon style={{ fontSize: 15 }} aria-hidden='true' />Incidents</SecLabel>
      <Stats>
        <StatCell icon={ReportProblemRoundedIcon} tint={O} value={i.open || 0} label='open' />
        <StatCell icon={CheckCircleRoundedIcon} tint={G} value={i.resolved || 0} label='resolved' />
        <StatCell icon={DoneAllRoundedIcon} tint={ink.mute} value={i.closed || 0} label='closed' />
      </Stats>
      {scope.report && (
        <TileBtn
          type='button'
          $compact={compact}
          style={compact ? undefined : { marginTop: 'auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
          onClick={() => navigate(scope.report)}
        >
          <ReportProblemRoundedIcon style={{ fontSize: 14 }} /> Report incident
        </TileBtn>
      )}
      <TileBtn type='button' $compact={compact} style={compact ? undefined : { marginTop: scope.report ? 6 : 'auto' }} onClick={() => navigate(scope.incidents)}>View incidents</TileBtn>
    </Section>
  ) : null

  const goRemedials = view => navigate(remedialsViewUrl(scope.remedialTarget, view))
  const remedialsSection = showTasks ? (
    <Section key='remedials' $compact={compact}>
      <SecLabel><ChecklistOutlinedIcon style={{ fontSize: 15 }} aria-hidden='true' />Remedials<RemedialCountsExplainer /></SecLabel>
      <Stats>
        <LinkStatCell icon={ScheduleRoundedIcon} tint={A} value={r.open || 0} label='open' hint='See the open remedials' onClick={() => goRemedials('all_open')} />
        <LinkStatCell icon={ErrorOutlineRoundedIcon} tint={R} value={r.overdue || 0} label='overdue' hint='See the overdue remedials' onClick={() => goRemedials('overdue')} />
        <LinkStatCell icon={HelpOutlineRoundedIcon} tint={O} value={r.needsDecision || 0} label='need a decision' hint='See the remedials nobody has acted on yet' onClick={() => goRemedials('needs_decision')} />
      </Stats>
      <TileBtn type='button' $compact={compact} onClick={() => goRemedials(DASHBOARD)}>View remedials</TileBtn>
    </Section>
  ) : null

  const parts = [hero, tasksSection, incidentsSection, remedialsSection].filter(Boolean)
  if (parts.length === 0) return null

  return (
    <Strip $compact={compact}>
      <Overview $compact={compact}>
        {parts.flatMap((p, idx) => (idx === 0 ? [p] : [<VDivider key={`div-${idx}`} />, p]))}
      </Overview>
    </Strip>
  )
}

export default CompliancePulse

const Strip = styled.div`
  ${({ $compact }) => ($compact ? '' : cardStyle)}
  padding: ${({ $compact }) => ($compact ? '0' : '14px 18px')};
  margin-top: ${({ $compact }) => ($compact ? '0' : '14px')};
  margin-bottom: ${({ $compact }) => ($compact ? '0' : '14px')};
`

const Overview = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${({ $compact }) => ($compact ? '16px' : '20px')};
  flex-wrap: wrap;
`

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '6px' : '10px')};
  flex: 1.6 1 ${({ $compact }) => ($compact ? '300px' : '340px')};
  min-width: ${({ $compact }) => ($compact ? '260px' : '300px')};

  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-width: 0;
  }
`

const HeroRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const HeroMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  flex: 1;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ $compact }) => ($compact ? '6px' : '10px')};
  flex: 1 1 ${({ $compact }) => ($compact ? '140px' : '160px')};
  min-width: ${({ $compact }) => ($compact ? '130px' : '150px')};

  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-width: 0;
  }
`

const VDivider = styled.div`
  width: 1px;
  align-self: stretch;
  background: ${line.base};
  @media (max-width: 860px) { display: none; }
`

const RingButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  transition: ${transition};
  &:hover { transform: translateY(-1px); }
`

const Ring = styled.div`
  width: ${({ $compact }) => ($compact ? '44px' : '56px')};
  height: ${({ $compact }) => ($compact ? '44px' : '56px')};
  border-radius: ${radius.round};
  background: ${({ $colour, $deg }) => `conic-gradient(${$colour} ${$deg}deg, ${line.base} 0)`};
  display: flex;
  align-items: center;
  justify-content: center;
`

const RingInner = styled.div`
  width: ${({ $compact }) => ($compact ? '33px' : '43px')};
  height: ${({ $compact }) => ($compact ? '33px' : '43px')};
  border-radius: ${radius.round};
  background: ${surface.card};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${font.small};
  font-weight: 800;
  color: ${ink.base};
  font-variant-numeric: tabular-nums;
`

const Band = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  text-transform: capitalize;
  color: ${({ $tint }) => $tint};
`

const SecLabel = styled.div`
  ${labelStyle}
  display: flex;
  align-items: center;
  gap: 7px;
`

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
`

const Cell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`

const LinkCell = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 40px;
  padding: 0 8px;
  margin: 0 -8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  cursor: pointer;
  &:hover { background: ${surface.wash}; text-decoration: underline; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 1px; }
`

const Num = styled.b`
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`

const Lbl = styled.span`
  font-size: 12px;
  color: ${ink.soft};
  font-weight: 600;
`

const SetupLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  flex-wrap: wrap;
  row-gap: 6px;
`

const SetupLabel = styled.div`
  ${labelStyle}
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
`

const Track = styled.div`
  flex: 1;
  min-width: 60px;
  height: 7px;
  border-radius: ${radius.pill};
  background: ${surface.wash};
  overflow: hidden;

  flex: 1 1 60px;
  min-width: 48px;
`

const Fill = styled.div`
  height: 100%;
  border-radius: ${radius.pill};
  background: ${({ theme }) => theme.colors.secondary};
  transition: ${transition};
`

const SetupValue = styled.span`
  font-size: ${font.caption};
  font-weight: 800;
  color: ${ink.base};
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`

const Warning = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${font.caption};
  font-weight: 700;
  color: ${RAG.critical.fg};
`

const TileBtn = styled.button`
  margin-top: auto;
  align-self: stretch;
  padding: 7px 12px;
  border-radius: ${radius.sm};
  border: 1px solid ${line.base};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.caption};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};
  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.secondary};
  }

  ${({ $compact }) => $compact && `
    margin-top: auto;
    padding-top: 8px;
    align-self: flex-start;
    padding: 2px 0;
    border: none;
    border-radius: 0;
    background: transparent;
    color: #1f8c97;
    font-size: 12.5px;

    &::after { content: ' \\2192'; }

    &:hover {
      color: #1f8c97;
      text-decoration: underline;
    }
  `}
`

const PrimaryBtn = styled(TileBtn)`
  background: ${({ theme }) => theme.colors.secondary};
  border-color: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  &:hover { filter: brightness(1.06); color: #fff; }
`

const SkeletonRing = styled.div`
  width: ${({ $compact }) => ($compact ? '44px' : '56px')};
  height: ${({ $compact }) => ($compact ? '44px' : '56px')};
  border-radius: ${radius.round};
  background: ${surface.wash};
  flex-shrink: 0;
`

const SkeletonBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SkeletonLine = styled.div`
  height: 12px;
  border-radius: ${radius.pill};
  background: ${surface.wash};
`
